import React, { useEffect, useCallback, useRef, useState } from 'react';
import * as yup from 'yup';
import { TextField } from '@mui/material';
import { FormTextField } from 'components/common/form/FormTextField';
import { useForm, type FieldPath } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import notify from 'lib/notify';

type FormData = {
  links: string[] | undefined;
};

type InlineConfigBase<V> = {
  value: V;
  onChange: (v: V) => void;
  onCommit: (v: V) => Promise<boolean> | void;
  onCancel: () => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
};

type LinksInlineConfig = InlineConfigBase<string[]>;

const schema = yup.object().shape({
  links: yup.array(),
});

const EMPTY_LINKS_ARRAY: string[] = [];

const DUPLICATE_LINK_MESSAGE = 'This link is already added';

const duplicateLinkIndexes = (links: string[]) => {
  const firstIndexByLink = new Map<string, number>();
  const duplicates = new Set<number>();

  links.forEach((link, index) => {
    const trimmed = link.trim();
    if (!trimmed) {
      return;
    }

    const firstIndex = firstIndexByLink.get(trimmed);
    if (firstIndex == null) {
      firstIndexByLink.set(trimmed, index);
      return;
    }

    duplicates.add(index);
  });

  return duplicates;
};

const linksForSave = (links: string[]) => {
  const duplicates = duplicateLinkIndexes(links);
  return links.filter((link, index) => link !== '' && !duplicates.has(index));
};

const withTrailingEmptyLink = (links: string[]) => (links.some(link => link === '') ? links : [...links, '']);

const SocialMediaLinksManager = ({
  initialLinks = EMPTY_LINKS_ARRAY,
  onChange,
  triggerSave,
  inlineConfig,
}: {
  initialLinks?: string[];
  onChange?: (links: string[]) => void;
  triggerSave?: () => void;
  inlineConfig?: LinksInlineConfig;
}) => {
  const isInline = !!inlineConfig;
  const inputRef = useRef<HTMLInputElement | null>(null);

  // ---------- Non-inline state (react-hook-form) ----------
  const { control, setValue, getValues, watch, trigger, setError, clearErrors } = useForm<
    FormData,
    unknown,
    yup.InferType<typeof schema>
  >({
    defaultValues: {
      links: withTrailingEmptyLink(
        initialLinks.filter(link => link && (link.includes('http://') || link.includes('https://'))),
      ),
    },
    resolver: yupResolver(schema),
  });

  const formLinks = watch('links') || [];

  // ---------- Inline state ----------
  const [inlineLinks, setInlineLinks] = useState<string[]>(() => withTrailingEmptyLink(inlineConfig?.value ?? []));
  const [inlineDuplicateIndexes, setInlineDuplicateIndexes] = useState<Set<number>>(() => new Set());

  const showDuplicateErrors = (nextLinks: string[]) => {
    const duplicates = duplicateLinkIndexes(nextLinks);

    if (isInline) {
      setInlineDuplicateIndexes(duplicates);
      return duplicates;
    }

    nextLinks.forEach((_, index) => {
      const fieldName = `links.${index}` as FieldPath<FormData>;
      if (duplicates.has(index)) {
        setError(fieldName, { type: 'duplicate', message: DUPLICATE_LINK_MESSAGE });
      } else {
        clearErrors(fieldName);
      }
    });

    return duplicates;
  };

  const publishLinks = (nextLinks: string[]) => {
    const duplicates = showDuplicateErrors(nextLinks);
    const saved = linksForSave(nextLinks);

    if (isInline) {
      inlineConfig?.onChange(saved);
    } else {
      onChange?.(saved);
    }

    return duplicates.size === 0;
  };

  useEffect(() => {
    if (!isInline) {
      return;
    }

    const base = inlineConfig!.value ?? [];
    setInlineLinks(prev => {
      const saved = linksForSave(prev);
      const incoming = base.filter(link => link !== '');
      const echoedSave = saved.length === incoming.length && saved.every((link, index) => link === incoming[index]);
      // The parent value omits duplicate rows. Keep those rows in the editor.
      if (echoedSave && duplicateLinkIndexes(prev).size > 0) {
        return withTrailingEmptyLink(prev);
      }

      return withTrailingEmptyLink(base);
    });
  }, [isInline, inlineConfig?.value]);

  useEffect(() => {
    if (isInline && inlineConfig?.autoFocus !== false) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isInline, inlineConfig?.autoFocus]);

  const links = isInline ? inlineLinks : formLinks;

  useEffect(() => {
    showDuplicateErrors(links);
  }, []);

  const addEmptyInput = useCallback(() => {
    if (isInline) {
      setInlineLinks(prev => {
        if (prev.some(l => l === '')) return prev;
        return [...prev, ''];
      });
    } else {
      const currentLinks = getValues('links') || [];
      if (!currentLinks.some(l => l === '')) {
        setValue('links', [...currentLinks, '']);
      }
    }
  }, [isInline, getValues, setValue]);

  useEffect(() => {
    if (!isInline) {
      addEmptyInput();
    }
  }, [initialLinks, addEmptyInput, isInline]);

  const removeLink = (index: number) => {
    if (isInline) {
      const newLinks = inlineLinks.filter((_, i) => i !== index);
      const withEmpty = withTrailingEmptyLink(newLinks);
      setInlineLinks(withEmpty);
      publishLinks(withEmpty);
    } else {
      const currentLinks = getValues('links') || [];
      const newLinks = currentLinks.filter((_, i) => i !== index);
      setValue('links', newLinks);
      publishLinks(newLinks);
    }
  };

  const onInputChange = async (index: number, value: string) => {
    if (value === '') {
      removeLink(index);
      return;
    }

    if (isInline) {
      const newLinks = [...inlineLinks];
      newLinks[index] = value;
      const withEmpty = withTrailingEmptyLink(newLinks);
      setInlineLinks(withEmpty);
      publishLinks(withEmpty);
    } else {
      const isValid = await trigger(`links.${index}`);
      const currentLinks = getValues('links') || [];
      const newLinks = [...currentLinks];
      newLinks[index] = value;
      setValue('links', newLinks);
      if (isValid) {
        publishLinks(newLinks);
      }
    }
  };

  const saveLinks = (nextLinks: string[]) => {
    if (duplicateLinkIndexes(nextLinks).size > 0) {
      showDuplicateErrors(nextLinks);
      notify(new Error(DUPLICATE_LINK_MESSAGE));
      return false;
    }

    return true;
  };

  return (
    <>
      {links.map((_link, index) => (
        <div key={index} style={{ position: 'relative', marginBottom: '8px' }}>
          {isInline ? (
            <TextField
              inputRef={index === 0 ? inputRef : undefined}
              size="small"
              fullWidth
              value={_link}
              onChange={e => onInputChange(index, e.target.value)}
              onFocus={addEmptyInput}
              onBlur={() => addEmptyInput()}
              onKeyDown={async e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!saveLinks(inlineLinks)) {
                    return;
                  }
                  await inlineConfig?.onCommit?.(linksForSave(inlineLinks));
                } else if (e.key === 'Escape') {
                  e.stopPropagation();
                  e.preventDefault();
                  inlineConfig?.onCancel();
                }
              }}
              disabled={inlineConfig?.disabled}
              error={inlineConfig?.hasError || inlineDuplicateIndexes.has(index)}
              helperText={inlineDuplicateIndexes.has(index) ? DUPLICATE_LINK_MESSAGE : undefined}
              placeholder="URL"
            />
          ) : (
            <FormTextField
              name={`links.${index}`}
              control={control}
              sx={{ width: '100%' }}
              onChange={e => onInputChange(index, e.target.value)}
              onFocus={addEmptyInput}
              onBlur={() => addEmptyInput()}
              onKeyDown={async e => {
                if (e.key === 'Enter' && triggerSave) {
                  const isValid = await trigger(`links.${index}`);
                  const currentLinks = getValues('links') || [];
                  if (isValid && saveLinks(currentLinks)) {
                    triggerSave();
                  }
                }
              }}
            />
          )}
        </div>
      ))}
    </>
  );
};

export default SocialMediaLinksManager;
